/**
 * useWaveRfpActions — encapsulates the 3 wave-launch API calls and
 * their loading/error/sent state. Extracted from BuildWavePage so the
 * orchestrator file stays focused on form state + routing.
 *
 *   • generateRfps    — POST /api/brand/waves/generate-rfps
 *   • previewBurstPlan— POST /api/brand/waves/{id}/preview-burst-plan
 *   • sendRfps        — POST /api/brand/waves/{id}/send
 *
 * Side-effect contract:
 *   • generateRfps creates a wave doc + populates {generatedRfps, waveId}
 *   • previewBurstPlan lazily creates the wave (if not already created)
 *     so the brand can preview before committing to send
 *   • sendRfps finalizes the wave and navigates the user to the live
 *     blueprint after a brief success animation
 *
 * All 3 follow the same pattern: clear errors, flip loading, call API,
 * handle response, finally release loading. Centralizing keeps the
 * error-handling surface single-sourced.
 */
import { useCallback, useState } from 'react';
import axios from 'axios';
import { buildRfpPayload } from './buildRfpPayload';

const API = process.env.REACT_APP_BACKEND_URL;

export function useWaveRfpActions({
  campaignGoal,
  budget,
  platforms,
  selectedCreatorList,
  selectedCreatorsCount,
  brief,
  briefClonedFrom,
  navigate,
}) {
  const [showRfpModal, setShowRfpModal] = useState(false);
  const [rfpLoading, setRfpLoading] = useState(false);
  const [rfpSending, setRfpSending] = useState(false);
  const [rfpSent, setRfpSent] = useState(false);
  const [rfpError, setRfpError] = useState(null);
  const [generatedRfps, setGeneratedRfps] = useState([]);
  const [waveId, setWaveId] = useState(null);
  const [footprintPulled, setFootprintPulled] = useState(false);
  const [footprintSummary, setFootprintSummary] = useState('');
  const [burstPlan, setBurstPlan] = useState(null);
  const [burstPlanLoading, setBurstPlanLoading] = useState(false);
  const [burstPlanError, setBurstPlanError] = useState(null);
  // B2 preflight — gates the Send button inside the RFP modal.
  const [preflight, setPreflight] = useState(null);
  const [preflightLoading, setPreflightLoading] = useState(false);

  const runPreflight = useCallback(async (targetWaveId) => {
    const wid = targetWaveId || waveId;
    if (!wid) return;
    setPreflightLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/brand/waves/${wid}/preflight`, {}, { withCredentials: true });
      setPreflight(data);
    } catch {
      // Non-fatal: surface an all-red shell so the brand can re-run.
      setPreflight({ all_passed: false, checks: [{ id: 'preflight', label: 'Preflight service', ok: false, reason: 'Could not run preflight — retry.' }] });
    } finally {
      setPreflightLoading(false);
    }
  }, [waveId]);

  const _generatePayload = useCallback(() => buildRfpPayload({
    campaignGoal,
    selectedCreatorList,
    budget,
    platforms,
    brief,
    briefClonedFrom,
  }), [campaignGoal, selectedCreatorList, budget, platforms, brief, briefClonedFrom]);

  const _hydrateRfps = useCallback((rfps) => rfps.map((rfp) => {
    const m = selectedCreatorList.find((c) => c.handle === rfp.creator_handle || c.name === rfp.creator_name);
    return { ...rfp, avatar: m?.avatar };
  }), [selectedCreatorList]);

  const handleGenerateRfps = useCallback(async () => {
    setRfpLoading(true);
    setRfpError(null);
    setGeneratedRfps([]);
    setWaveId(null);
    setRfpSent(false);
    setShowRfpModal(true);
    try {
      const { data } = await axios.post(
        `${API}/api/brand/waves/generate-rfps`,
        _generatePayload(),
        { withCredentials: true },
      );
      setGeneratedRfps(_hydrateRfps(data.rfps));
      setWaveId(data.wave_id);
      setFootprintPulled(!!data.footprint_pulled);
      setFootprintSummary(data.footprint_summary || '');
      // Auto-run the pre-send gate the moment the wave exists.
      setPreflight(null);
      runPreflight(data.wave_id);
    } catch (err) {
      const d = err.response?.data?.detail;
      setRfpError(typeof d === 'string' ? d : 'Failed to generate RFPs.');
    } finally {
      setRfpLoading(false);
    }
  }, [_generatePayload, _hydrateRfps, runPreflight]);

  const handleSendRfps = useCallback(async () => {
    if (!waveId) return;
    setRfpSending(true);
    setRfpError(null);
    try {
      await axios.post(`${API}/api/brand/waves/${waveId}/send`, {}, { withCredentials: true });
      setRfpSent(true);
      // iter 70: post-send redirect to the auto-generated personalized Wave Blueprint
      // — the brand's moment of "your plan is locked, here's the recipe." From there
      // they can click "Live Engine" to enter the Control Room.
      setTimeout(() => {
        setShowRfpModal(false);
        navigate(`/waves/${waveId}/blueprint`, {
          state: { justSentCreatorCount: selectedCreatorsCount },
        });
      }, 1200);
    } catch (err) {
      setRfpError(err.response?.data?.detail || 'Failed to send.');
    } finally {
      setRfpSending(false);
    }
  }, [waveId, selectedCreatorsCount, navigate]);

  // UX: user clicks the preview CTA on Step 3 → we silently generate a
  // wave (if none yet), then fetch the orchestration preview, without
  // opening the RFP modal. Lets the brand see the burst plan BEFORE
  // committing to send.
  const handlePreviewBurstPlan = useCallback(async () => {
    setBurstPlanLoading(true);
    setBurstPlanError(null);
    setBurstPlan(null);
    try {
      let currentWaveId = waveId;
      if (!currentWaveId) {
        const { data } = await axios.post(
          `${API}/api/brand/waves/generate-rfps`,
          _generatePayload(),
          { withCredentials: true },
        );
        currentWaveId = data.wave_id;
        setGeneratedRfps(_hydrateRfps(data.rfps));
        setWaveId(currentWaveId);
      }
      const { data: plan } = await axios.post(
        `${API}/api/brand/waves/${currentWaveId}/preview-burst-plan`,
        {},
        { withCredentials: true },
      );
      setBurstPlan(plan);
    } catch (err) {
      const d = err.response?.data?.detail;
      setBurstPlanError(typeof d === 'string' ? d : 'Failed to compute burst plan.');
    } finally {
      setBurstPlanLoading(false);
    }
  }, [waveId, _generatePayload, _hydrateRfps]);

  const handleRfpModalChange = useCallback((open) => {
    if (rfpSending || rfpLoading) return;
    setShowRfpModal(open);
    if (!open) {
      setRfpSent(false);
      setRfpError(null);
    }
  }, [rfpSending, rfpLoading]);

  return {
    // state
    showRfpModal,
    rfpLoading,
    rfpSending,
    rfpSent,
    rfpError,
    generatedRfps,
    waveId,
    footprintPulled,
    footprintSummary,
    burstPlan,
    burstPlanLoading,
    burstPlanError,
    preflight,
    preflightLoading,
    // actions
    handleGenerateRfps,
    handleSendRfps,
    handlePreviewBurstPlan,
    handleRfpModalChange,
    runPreflight,
  };
}
